<template>
  <FullScreenLayoutContent full-width class="lg:h-dvh lg:min-h-0 lg:overflow-hidden">
    <template #header>
      <DialogHeader v-if="data" :dialog="data.dialog" @close="exitFlow">
        <template #actions>
          <BaseTextButton
            v-if="data.dialog.phase === DialogPhase.Conversation && dialogRole === 'coach'"
            color="tonalPrimary"
            size="M"
            @click="openConductDialog"
            >Dialog abschliessen</BaseTextButton
          >
          <BaseTextButton
            v-if="data.dialog.phase === DialogPhase.Feedback && dialogRole === 'coachee'"
            color="tonal"
            size="M"
            icon="share"
            @click="openReleaseDialog"
            >{{ $t('dialog.release.action') }}</BaseTextButton
          >
          <BaseTextButton
            v-if="data.dialog.phase === DialogPhase.CoachPreparation && dialogRole === 'coach'"
            color="tonal"
            size="M"
            icon="play_arrow"
            @click="openCoachReleaseDialog"
            >{{ $t('dialog.releaseCoach.action') }}</BaseTextButton
          >

          <BaseIconButton
            v-if="
              (dialogRole === 'coach' &&
                data.dialog.phase != DialogPhase.Feedback &&
                data.dialog.phase != DialogPhase.Conversation) ||
              dialogRole === 'coachee'
            "
            v-tooltip.bottom="$t('dialog.download.tooltip')"
            icon="download"
            size="M"
            width="narrow"
            color="outlined"
            @click="openExportDialog"
            :loading="isDownloading"
          />
          <BaseMenu
            v-if="menuItems.length > 0"
            width=""
            size="M"
            :items="menuItems"
            v-slot="{ open }"
          >
            <BaseIconButton icon="more_vert" size="M" width="narrow" :force-pressed="open" />
          </BaseMenu>
        </template>
      </DialogHeader>
    </template>
    <LoadingWrapper :state="status">
      <DialogSlideNavigation
        :key="`${data.dialog.id}-${data.dialog.phase}`"
        :coachee-id="data.dialog.coachee.id"
        :feedback-topics="data.feedbackTopics"
        :is-editable="isEditable"
        :dialog-role="dialogRole"
        :dialog="data.dialog"
      />
      <ConductDialogDialog
        @close="closeConductDialog"
        @conduct-dialog="conductDialog"
        :is-open="isConductOpen"
        :uncovered-count="uncoveredReviewCount"
      />
      <RetractDialogDialog
        @close="closeRetractDialog"
        @retract-dialog="retractDialog"
        :is-open="isRetractOpen"
        :coachee-name="data.dialog.coachee.firstName ?? ''"
      />
      <ReleaseDialogDialog
        @close="closeReleaseDialog"
        @release-dialog="releaseDialog"
        :is-open="isReleaseOpen"
        :disabled="!canReleaseDialog || releaseStatus == ApiRequestStatus.Loading"
        :unfinished-topics="unfinishedTopics"
        :has-unfinished-surveys="unfinishedSurveys.length > 0"
        :coach="data.dialog.coach"
      />
      <CoachReleaseDialogDialog
        @close="closeCoachReleaseDialog"
        @coach-release-dialog="onCoachRelease"
        :is-open="isCoachReleaseOpen"
        :disabled="coachReleaseStatus == ApiRequestStatus.Loading"
      />
      <CoachRetractDialogDialog
        @close="closeCoachRetractDialog"
        @coach-retract-dialog="onCoachRetract"
        :is-open="isCoachRetractOpen"
        :disabled="coachRetractStatus == ApiRequestStatus.Loading"
      />
      <DialogExportDialog
        :is-open="isExportOpen"
        :dialog-phase="data.dialog.phase"
        :is-downloading="isDownloading"
        @close="closeExportDialog"
        @submit="onExport"
      />
    </LoadingWrapper>
  </FullScreenLayoutContent>
</template>
<script lang="ts" setup>
  import FullScreenLayoutContent from '../../../core/components/layouts/FullScreenLayoutContent.vue'
  import LoadingWrapper from '../../../core/components/loading/LoadingWrapper.vue'
  import DialogHeader from '../components/DialogHeader.vue'
  import DialogSlideNavigation from '../components/DialogSlideNavigation.vue'
  import { useReleasedDialog } from '../composable/use-released-dialog'
  import { DialogPhase } from '../../../core/models/dialog/dialog'
  import { computed, provide } from 'vue'
  import { useLocalStorage } from '@vueuse/core'
  import { useUserStore } from '../../../stores/user.store'
  import BaseTextButton from '@/core/components/base/button/text-button/BaseTextButton.vue'
  import { useDialog } from '@/core/components/base/dialog/use-dialog'
  import ConductDialogDialog from '../components/ConductDialogDialog.vue'
  import { useFlowHistory } from '@/core/composables/use-flow-history'
  import ReleaseDialogDialog from '../components/ReleaseDialogDialog.vue'
  import CoachReleaseDialogDialog from '../components/CoachReleaseDialogDialog.vue'
  import CoachRetractDialogDialog from '../components/CoachRetractDialogDialog.vue'
  import { ApiRequestStatus } from '@/core/models/api/api-request-status'
  import BaseMenu from '@/core/components/base/menu/BaseMenu.vue'
  import BaseIconButton from '@/core/components/base/button/icon-button/BaseIconButton.vue'
  import RetractDialogDialog from '../components/RetractDialogDialog.vue'
  import DialogExportDialog, {
    type CommentScope,
  } from '../components/DialogExportDialog.vue'
  import type { MenuItemGroup, SingleMenuItem } from '@/core/components/base/menu/MenuItem.ts'
  import { useI18n } from 'vue-i18n'
  import { useFileDownload } from '@/core/composables/use-download-file.ts'
  import { useRoute } from 'vue-router'
  import { hideUnsharedCommentsKey } from '../composable/use-dialog-navigation'
  import { useObjectives } from '@/core/composables/use-objectives'

  const {
    data,
    status,
    conductDialog,
    retractDialog,
    unfinishedTopics,
    unfinishedSurveys,
    releaseDialog,
    canReleaseDialog,
    releaseStatus,
    coachReleaseDialog,
    coachReleaseStatus,
    coachRetractDialog,
    coachRetractStatus,
  } = useReleasedDialog()

  const { uncoveredReviewCount } = useObjectives(() => ({
    dialogId: data.value?.dialog.id ?? '',
    ownerId: data.value?.dialog.coachee.id ?? '',
  }))

  const { user } = useUserStore()

  const {
    isOpen: isReleaseOpen,
    closeDialog: closeReleaseDialog,
    openDialog: openReleaseDialog,
  } = useDialog()
  const {
    isOpen: isCoachReleaseOpen,
    closeDialog: closeCoachReleaseDialog,
    openDialog: openCoachReleaseDialog,
  } = useDialog()
  const {
    isOpen: isConductOpen,
    closeDialog: closeConductDialog,
    openDialog: openConductDialog,
  } = useDialog()
  const {
    isOpen: isRetractOpen,
    closeDialog: closeRetractDialog,
    openDialog: openRetractDialog,
  } = useDialog()
  const {
    isOpen: isCoachRetractOpen,
    closeDialog: closeCoachRetractDialog,
    openDialog: openCoachRetractDialog,
  } = useDialog()
  const {
    isOpen: isExportOpen,
    closeDialog: closeExportDialog,
    openDialog: openExportDialog,
  } = useDialog()

  const { exitFlow } = useFlowHistory({ name: 'dialogs' })

  const onCoachRelease = async () => {
    if (await coachReleaseDialog()) {
      closeCoachReleaseDialog()
    }
  }

  const onCoachRetract = async () => {
    if (await coachRetractDialog()) {
      closeCoachRetractDialog()
    }
  }

  const isEditable = computed(() => {
    return (
      (data.value?.dialog.phase == DialogPhase.Feedback &&
        data.value.dialog.coachee.id == user.id) ||
      (data.value?.dialog.phase != DialogPhase.Conducted && data.value.dialog.coach.id == user.id)
    )
  })

  const canEditObjectives = computed(
    () =>
      data.value.dialog.coachee.id != user.id &&
      (data.value?.dialog.phase === DialogPhase.CoachPreparation ||
        data.value?.dialog.phase === DialogPhase.Conversation),
  )

  const dialogRole = computed(() => {
    return data.value?.dialog.coach.id == user.id ? 'coach' : 'coachee'
  })

  provide('canEditObjectives', canEditObjectives)

  const hideUnsharedComments = useLocalStorage('hide-unshared-comments', false)
  provide(hideUnsharedCommentsKey, hideUnsharedComments)

  const route = useRoute()
  const { downloadFile, isDownloading } = useFileDownload(`dialog/${route.params.id}/pdf`)

  const onExport = async (commentScope: CommentScope) => {
    const success = await downloadFile({ query: { commentScope } })
    if (success) {
      closeExportDialog()
    }
  }

  const { t } = useI18n()

  const menuItems = computed(() => {
    const items: MenuItemGroup[] = []

    if (dialogRole.value === 'coach') {
      if (
        data.value.dialog.phase == DialogPhase.CoachPreparation ||
        data.value.dialog.phase == DialogPhase.Conversation
      ) {
        const subItems: SingleMenuItem[] = []
        subItems.push({
          id: 'retract-coachee',
          title: t('dialog.retract', { firstName: data.value.dialog.coachee.firstName ?? '' }),
          leadingIcon: 'undo',
          callback: openRetractDialog,
        })
        if (data.value.dialog.phase == DialogPhase.Conversation) {
          subItems.push({
            id: 'retract',
            title: t('dialog.coachRetract.action'),
            leadingIcon: 'undo',
            callback: openCoachRetractDialog,
          })
        }
        items.push({ items: subItems })
      }
      if (data.value.dialog.phase == DialogPhase.Conversation) {
        items.push({
          items: [
            {
              id: 'hide-unshared-comments',
              title: t(
                'comment.hideUnshared.' + (hideUnsharedComments.value ? 'showAction' : 'hideAction'),
              ),
              leadingIcon: hideUnsharedComments.value ? 'visibility' : 'visibility_off',
              selected: hideUnsharedComments.value,
              callback: () => {
                hideUnsharedComments.value = !hideUnsharedComments.value
              },
            },
          ],
        })
        items.push({
          items: [
            {
              id: 'download',
              title: t('dialog.download.action'),
              leadingIcon: 'download',
              trailingText: t('dialog.download.file'),
              callback: openExportDialog,
            },
          ],
        })
      }
    }

    return items
  })
</script>
